/**
 * December advent: 25 daily scenes and the countdown to Christmas.
 */
import { monthOverride } from './seasons';

export type AdventMotion = 'rise' | 'spin' | 'swing' | 'pop' | 'glow' | 'shake' | 'fall';

export interface AdventScene {
  day: number;
  label: string;
  glyph: string;
  motion: AdventMotion;
}

export const ADVENT_MS = 8000;

export const ADVENT: readonly AdventScene[] = [
  { day: 1, label: 'Tree star', glyph: '⭐', motion: 'rise' },
  { day: 2, label: 'Candy cane', glyph: '🍬', motion: 'spin' },
  { day: 3, label: 'Bells', glyph: '🔔', motion: 'swing' },
  { day: 4, label: 'Stockings', glyph: '🧦', motion: 'pop' },
  { day: 5, label: 'Gingerbread', glyph: '🍪', motion: 'spin' },
  { day: 6, label: 'Nutcracker', glyph: '💂', motion: 'pop' },
  { day: 7, label: 'Snowman', glyph: '⛄', motion: 'rise' },
  { day: 8, label: 'Wreath', glyph: '🎀', motion: 'swing' },
  { day: 9, label: 'Carolers', glyph: '🎶', motion: 'pop' },
  { day: 10, label: 'Lights', glyph: '💡', motion: 'glow' },
  { day: 11, label: 'Cocoa', glyph: '☕', motion: 'rise' },
  { day: 12, label: 'Gift', glyph: '🎁', motion: 'pop' },
  { day: 13, label: 'Angel', glyph: '👼', motion: 'rise' },
  { day: 14, label: 'Drum', glyph: '🥁', motion: 'shake' },
  { day: 15, label: 'Candle', glyph: '🕯️', motion: 'glow' },
  { day: 16, label: 'Holly', glyph: '🌿', motion: 'swing' },
  { day: 17, label: 'Ornament', glyph: '🔮', motion: 'fall' },
  { day: 18, label: 'North Pole', glyph: '🪧', motion: 'pop' },
  { day: 19, label: 'Elves', glyph: '🧝', motion: 'pop' },
  { day: 20, label: 'Cookie', glyph: '🥛', motion: 'swing' },
  { day: 21, label: 'Sleigh bells', glyph: '🎵', motion: 'shake' },
  { day: 22, label: 'Star', glyph: '🌟', motion: 'glow' },
  { day: 23, label: 'Reindeer', glyph: '🦌', motion: 'rise' },
  { day: 24, label: 'Santa packing', glyph: '🎅', motion: 'pop' },
  { day: 25, label: 'Merry Christmas', glyph: '🎄', motion: 'glow' },
];

const listeners = new Set<(day: number) => void>();

export function playAdventDay(day: number): void {
  listeners.forEach((fn) => fn(day));
}

export function onAdventPlay(fn: (day: number) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function adventDayOverride(search: string): number | null {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(
    'advent'
  );
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 25) return null;
  return n;
}

/** Date used for previews. `?month=12&advent=14` is December 14 of this year. */
export function themeDate(now: Date, search: string): Date {
  const month = monthOverride(search);
  const advent = adventDayOverride(search);
  const m = (month ?? now.getMonth() + 1) - 1;
  const day = advent ?? now.getDate();
  return new Date(now.getFullYear(), m, day);
}

export function adventPlayedKey(year: number, day: number): string {
  return `${year}-12-${String(day).padStart(2, '0')}`;
}

export function christmasCountdown(date: Date): { days: number; label: string } {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const christmas = new Date(date.getFullYear(), 11, 25);
  const days = Math.round((christmas.getTime() - start.getTime()) / 86_400_000);
  if (days <= 0) return { days: 0, label: 'Merry Christmas' };
  if (days === 1) return { days: 1, label: '1 day until Christmas' };
  return { days, label: `${days} days until Christmas` };
}

export function decemberCountdownLabel(now: Date, search: string): string | null {
  const month = monthOverride(search) ?? now.getMonth() + 1;
  if (month !== 12) return null;
  return christmasCountdown(themeDate(now, search)).label;
}

export function adventScene(day: number): AdventScene | null {
  return ADVENT.find((scene) => scene.day === day) ?? null;
}
